import 'zone.js/plugins/task-tracking';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import {
  NgModule,
  NgZone,
  inject,
  PLATFORM_ID,
  APP_INITIALIZER,
  Injector,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, UrlSerializer } from '@angular/router';
import { AuthModule, AuthConfig } from '@auth0/auth0-angular';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { RouterRepository } from '@ngneat/elf-ng-router-store';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
  TranslateStore,
} from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';

import { auth0InitializerFactory } from '@digital-platform/aura-authentication/services';
import { AuraAuthService } from '@digital-platform/aura-authentication/services';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FeatureEinsteinChatbotComponent } from '@digital-platform/rs-chatbot/feature-einstein-chatbot';
import { ShopGateService } from '@digital-platform/rs-cms/services';
import {
  urlConstants,
  urlConstantsToken,
} from '@digital-platform/rs-frontend/domain';
import { Environment } from '@digital-platform/rs-shared/domain';
import {
  ENVIRONMENT,
  SpinnerInterceptor,
  translationInitializerFactory,
  TranslationLoaderService,
  ViewerCountryInterceptor,
} from '@digital-platform/rs-shared/services';
import { TrustarcCmComponent } from '@digital-platform/rs-shared/ui';
import { RsSharedUiBrandModule } from '@digital-platform/rs-shared/ui-brand';
import { AuthHeaderInterceptor } from '@digital-platform/rs-signup/services';
import {
  FeatureFlagService,
  STATIC_FEATURE_FLAGS,
  featureFlagInitializerFactory,
} from '@digital-platform/util-feature-flags/services';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { CaseInsensitiveUrlSerializer } from './case-insensitive-url-serializer';
import { routes } from './routes';
import staticFeatureFlags from './static-feature-flags.json';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      paramsInheritanceStrategy: 'always',
      onSameUrlNavigation: 'reload',
    }),
    NgbModule,
    RsSharedUiBrandModule,
    TrustarcCmComponent,
    FeatureEinsteinChatbotComponent,
    TranslateModule.forRoot({
      loader: {
        deps: [HttpClient, urlConstantsToken],
        provide: TranslateLoader,
        useClass: TranslationLoaderService,
      },
    }),
    AuthModule.forRoot(environment.tenantConfig as AuthConfig),
  ],
  bootstrap: [AppComponent],
  providers: [
    RouterModule,
    TranslateStore,
    {
      provide: ENVIRONMENT,
      useFactory: () => Environment.parse(environment),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ViewerCountryInterceptor,
      multi: true,
    },
    {
      deps: [TranslateService, CookieService, Injector],
      multi: true,
      provide: APP_INITIALIZER,
      useFactory: translationInitializerFactory,
    },
    TranslateService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHeaderInterceptor,
      multi: true,
    },
    { provide: urlConstantsToken, useValue: urlConstants },

    FeatureFlagService,
    // Setting basic static flags, so the app has them available immediately.
    // Will be updated after FF load occurs
    {
      provide: STATIC_FEATURE_FLAGS,
      useValue: staticFeatureFlags,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: featureFlagInitializerFactory,
      deps: [FeatureFlagService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: auth0InitializerFactory,
      deps: [AuraAuthService, PLATFORM_ID],
      multi: true,
    },
    ShopGateService,
    {
      provide: UrlSerializer,
      useClass: CaseInsensitiveUrlSerializer,
    },

    // commenting out the URL serializer for now, as it is not needed, further research needed
    // {
    //   provide: UrlSerializer,
    //   useClass: OutletUrlSerializer,
    // },
  ],
})
export class AppModule {
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private ngZone = inject(NgZone);
  private routerRepository = inject(RouterRepository);

  constructor() {
    // we only want to do this on the browser side, not the server side
    if (isPlatformBrowser(this.platformId)) {
      const pageSpinner = this.document.getElementById('page-spinner');
      // show the page spinner only if it's not already shown
      if (pageSpinner && !pageSpinner.classList.contains('active')) {
        pageSpinner.classList.add('active');
      }
    }

    if (isPlatformServer(this.platformId)) {
      // Clear elf router state on server side to prevent an infinite navigation loop
      // @see https://github.com/ngneat/elf-ng-router-store/issues/22
      this.routerRepository.update({ state: null, navigationId: null });
    }

    // This code is commented out and left here intentionally, as it is useful for
    // debugging issues with SSR rendering stalling out.
    // /**
    //  * CONFIGURE how long to wait (in seconds)
    //  * before the pending tasks are dumped to the console.
    //  */
    // const WAIT_SECONDS = 10;

    // console.log(
    //   `⏳ ... Wait ${WAIT_SECONDS} seconds to dump pending tasks ... ⏳`
    // );

    // // Run the debugging `setTimeout` code outside of
    // // the Angular Zone, so it's not considered as
    // // yet another pending Zone Task:
    // this.ngZone.runOutsideAngular(() => {
    //   setTimeout(() => {
    //     // Access the NgZone's internals - TaskTrackingZone:
    //     const TaskTrackingZone = (this.ngZone as any)._inner._parent._properties
    //       .TaskTrackingZone;

    //     // Print to the console all pending tasks
    //     // (micro tasks, macro tasks and event listeners):
    //     console.debug('👀 Pending tasks in NgZone: 👀');

    //     const microTasks = TaskTrackingZone.getTasksFor('microTask');
    //     const macroTasks = TaskTrackingZone.getTasksFor('macroTask');
    //     const eventTasks = TaskTrackingZone.getTasksFor('eventTask');

    //     console.debug({
    //       microTasks: microTasks.length,
    //       macroTasks: macroTasks.length,
    //       eventTasks: eventTasks.length,
    //     });

    //     console.debug({
    //       microTasks,
    //       macroTasks,
    //       eventTasks,
    //     });

    //     // Advice how to find the origin of Zone tasks:
    //     console.debug(
    //       `👀 For every pending Zone Task listed above investigate the stacktrace in the property 'creationLocation' 👆`
    //     );
    //   }, 1000 * WAIT_SECONDS);
    // });
  }
}
